import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { en } from './locales/en';
import { fr, frPresets } from './locales/fr';

export type Locale = 'en' | 'fr';

/**
 * English is the source of truth and every key must exist in it. Other
 * languages are partial: an untranslated key falls back to English, so adding
 * a string never breaks a locale that has not caught up.
 */
export type Dictionary = Record<keyof typeof en, string>;

const DICTIONARIES: Record<Locale, Partial<Dictionary>> = { en, fr };

/** Preset labels per locale. English needs none — the presets are English. */
const PRESETS: Record<Locale, Record<string, string>> = { en: {}, fr: frPresets };

export const LOCALES: Array<{ id: Locale; label: string; long: string }> = [
  { id: 'en', label: 'EN', long: 'English' },
  { id: 'fr', label: 'FR', long: 'Français' },
];

const KEY = 'studio.locale';

const read = (): Locale => {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'en' || saved === 'fr') return saved;
  } catch {
    // Private windows throw on access; fall through to the browser's setting.
  }
  return navigator.language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
};

/** The interface's language right now, for calls made outside React. */
export const currentLocale = (): Locale => read();

/**
 * `t('some.key')`, with `{name}` placeholders filled from the second argument.
 *
 * A missing French string falls back to the English one rather than showing a
 * key: a half-translated screen is usable, a screen of dotted identifiers is
 * not — and it means adding a string never breaks the other language.
 */
export type Translate = (key: string, vars?: Record<string, string | number>) => string;

interface Ctx {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: Translate;
  /**
   * For text that already exists in English in the data — preset names and
   * descriptions, which live in `@studio/shared` because the prompts are built
   * from the same objects. Translating them here keeps the prompts English,
   * where the renderers work best, while the labels follow the interface.
   */
  tp: (key: string, fallback: string) => string;
}

const I18nContext = createContext<Ctx>({
  locale: 'en',
  setLocale: () => {},
  t: (key) => key,
  tp: (_key, fallback) => fallback,
});

export const useI18n = () => useContext(I18nContext);
export const useT = (): Translate => useContext(I18nContext).t;
export const usePresetLabel = () => useContext(I18nContext).tp;

function lookup(dict: Partial<Dictionary>, key: string): string | undefined {
  const value = (dict as unknown as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setState] = useState<Locale>(read);

  useEffect(() => {
    document.documentElement.lang = locale;
    try {
      localStorage.setItem(KEY, locale);
    } catch {
      // A preference that cannot be saved still applies for this session.
    }
  }, [locale]);

  const t = useCallback<Translate>(
    (key, vars) => {
      const dict = DICTIONARIES[locale];
      const raw = lookup(dict, key) ?? lookup(en, key) ?? key;
      if (!vars) return raw;
      return raw.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars ? String(vars[name]) : whole));
    },
    [locale],
  );

  const tp = useCallback((key: string, fallback: string) => PRESETS[locale][key] ?? fallback, [locale]);

  const value = useMemo(
    () => ({ locale, setLocale: (next: Locale) => setState(next), t, tp }),
    [locale, t, tp],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Plural helper for the handful of counts on screen.
 *
 * English and French happen to agree on the shape here — one versus many — so
 * one function covers both; anything more would need real CLDR rules.
 */
export function plural(t: Translate, n: number, one: string, many: string): string {
  return t(n === 1 ? one : many, { n });
}
